import React, { useCallback, useMemo } from 'react';
import { Select, Input, Button, Space } from 'antd';
import { PlusOutlined, MinusOutlined } from '@ant-design/icons';
import { t } from 'i18n';

const getGroupKey = (r: { destinationGroupId?: string; destination: string; key: string }) =>
    r.destinationGroupId ?? r.destination ?? r.key;

/** 同组内已使用的 unit → 返回第一个可用 unit */
const getFirstAvailableUnit = (
    usedUnits: string[],
    options: readonly { value: string }[]
): string => options.find((opt) => !usedUnits.includes(opt.value))?.value ?? options[0].value;

export const CONTAINER_UNIT_OPTIONS = [
    { value: 'PALLET_BOX', labelKey: 'tms.task.drawer.containerUnit.palletBox' },
    { value: 'BULK_BAG', labelKey: 'tms.task.drawer.containerUnit.bulkBag' },
    { value: 'PALLET', labelKey: 'tms.task.drawer.containerUnit.pallet' },
    { value: 'GAYLORD', labelKey: 'tms.task.drawer.containerUnit.gaylord' },
] as const;

const UNIT_OPTIONS_WITH_LABELS = CONTAINER_UNIT_OPTIONS.map((opt) => ({
    label: String(t(opt.labelKey)),
    value: opt.value,
}));

export type ContainerUnitType = (typeof CONTAINER_UNIT_OPTIONS)[number]['value'];

export interface ContainerInfoRow {
    key: string;
    destination: string;
    quantity: number;
    unit: ContainerUnitType | '';
    /** true = 本行显示目的地下拉框（按钮1“添加目的地”）；不设 = 无目的地时只显示数量+单位（按钮2“添加容器数量单位”） */
    showDestination?: boolean;
    /** 目的地分组 id：“添加目的地”新增行为新组，与上一组无关；同组内 unit 不可重复 */
    destinationGroupId?: string;
}

interface ContainerInfoTableProps {
    dataSource: ContainerInfoRow[];
    onChange?: (data: ContainerInfoRow[]) => void;
    destinationOptions: { label: string; value: string }[];
    readOnly?: boolean;
    /** 是否为卸车场景，true 时不展示底部「添加目的地」按钮。 */
    unloading?: boolean;
    /** 目的地列只读（显示 sortingCode 静态文本），同时隐藏所有添加容器按钮；数量和单位仍按 readOnly 控制。 */
    readOnlyDestination?: boolean;
}

const ContainerInfoTable: React.FC<ContainerInfoTableProps> = ({
    dataSource,
    onChange,
    destinationOptions,
    readOnly = false,
    unloading,
    readOnlyDestination = false,
}) => {
    const handleDelete = useCallback(
        (key: string) => {
            onChange?.(dataSource.filter((row) => row.key !== key));
        },
        [dataSource, onChange]
    );

    const updateRow = useCallback(
        (key: string, update: Partial<ContainerInfoRow>) => {
            onChange?.(dataSource.map((item) => (item.key === key ? { ...item, ...update } : item)));
        },
        [dataSource, onChange]
    );

    /** 预计算：groupKey、分组、组首行索引、每行「其他行已选目的地」 */
    const { groupKeyMap, groupsMap, firstRowInGroupIndexMap, usedDestinationsByRow } = useMemo(() => {
        const keyMap = new Map<string, string>();
        const grpMap = new Map<string, ContainerInfoRow[]>();
        const firstIndexMap = new Map<string, number>();
        dataSource.forEach((r, i) => {
            const gk = getGroupKey(r);
            keyMap.set(r.key, gk);
            const list = grpMap.get(gk) ?? [];
            list.push(r);
            grpMap.set(gk, list);
            if (!firstIndexMap.has(gk)) firstIndexMap.set(gk, i);
        });

        const usedByRow = new Map<string, Set<string>>();
        dataSource.forEach((row) => {
            const used = new Set<string>();
            dataSource.forEach((r) => {
                if (r.key !== row.key && r.destination) used.add(r.destination);
            });
            usedByRow.set(row.key, used);
        });

        return {
            groupKeyMap: keyMap,
            groupsMap: grpMap,
            firstRowInGroupIndexMap: firstIndexMap,
            usedDestinationsByRow: usedByRow,
        };
    }, [dataSource]);

    /** 修改目的地下拉时，需同步更新同组内所有行的 destination，避免组被拆成两个 */
    const handleDestinationChange = useCallback(
        (row: ContainerInfoRow, newDestination: string) => {
            const groupKey = getGroupKey(row);
            const groupRows = groupsMap.get(groupKey) ?? [];
            const groupKeys = new Set(groupRows.map((r) => r.key));
            const updated = dataSource.map((item) =>
                groupKeys.has(item.key) ? { ...item, destination: newDestination } : item
            );
            onChange?.(updated);
        },
        [dataSource, onChange, groupsMap]
    );

    /** 按钮1“添加目的地”：新的一组，与上一组无关；新组默认 Pallet Box */
    const handleAddOtherDestination = useCallback(() => {
        const newRow: ContainerInfoRow = {
            key: `r-${Date.now()}`,
            destination: '',
            quantity: 1,
            unit: CONTAINER_UNIT_OPTIONS[0].value,
            showDestination: true,
            destinationGroupId: `g-${Date.now()}`,
        };
        onChange?.([...dataSource, newRow]);
    }, [dataSource, onChange]);

    /** 按钮2“添加容器数量单位”：同行目的地组内加一行，继承 destinationGroupId 和 destination */
    const handleAddSameDestination = useCallback(
        (row: ContainerInfoRow) => {
            const groupKey = getGroupKey(row);
            const usedUnits = (groupsMap.get(groupKey) ?? []).map((r) => r.unit);
            const defaultUnit = getFirstAvailableUnit(usedUnits, CONTAINER_UNIT_OPTIONS) as ContainerUnitType;

            const newRow: ContainerInfoRow = {
                key: `r-${Date.now()}`,
                destination: row.destination,
                quantity: 1,
                unit: defaultUnit,
                destinationGroupId: row.destinationGroupId ?? (row.destination || undefined),
            };
            const idx = dataSource.findIndex((r) => r.key === row.key);
            const next =
                idx < 0
                    ? [...dataSource, newRow]
                    : [...dataSource.slice(0, idx + 1), newRow, ...dataSource.slice(idx + 1)];
            onChange?.(next);
        },
        [dataSource, onChange, groupsMap]
    );

    const renderRow = (row: ContainerInfoRow, index: number) => {
        const groupKey = groupKeyMap.get(row.key) ?? getGroupKey(row);
        const groupRows = groupsMap.get(groupKey) ?? [];
        const firstRowInGroupIndex = firstRowInGroupIndexMap.get(groupKey) ?? -1;
        const isFirstRowOfGroup = firstRowInGroupIndex === index;

        const isReadLike = readOnly || unloading || readOnlyDestination;
        const destinationLabel =
            destinationOptions.find((opt) => opt.value === row.destination)?.label ?? row.destination ?? '-';
        const destinationDisplay = isReadLike
            ? isFirstRowOfGroup
                ? destinationLabel
                : ''
            : null;
        /** 仅每组第一行显示目的地下拉框（”添加目的地”新行 或 组内首行）；行内添加的行不显示 */
        const showDestinationDropdown = row.showDestination === true || isFirstRowOfGroup;

        const usedDestinations = usedDestinationsByRow.get(row.key);
        const availableDestinationOptions = destinationOptions.filter(
            (opt) => opt.value === row.destination || !usedDestinations?.has(opt.value)
        );

        const destinationInput = isReadLike ? (
            <span className="text-gray-700">{destinationDisplay}</span>
        ) : showDestinationDropdown ? (
            <Select
                style={{ width: '100%' }}
                value={row.destination || undefined}
                onChange={(val) => handleDestinationChange(row, val || '')}
                options={availableDestinationOptions}
                placeholder={String(t('common.pleaseSelect'))}
                allowClear
                showSearch
                filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
            />
        ) : (
            <span className="text-gray-400"></span>
        );

        const quantityInput = readOnly ? (
            <span className="text-gray-700">{row.quantity || '-'}</span>
        ) : (
            <Input
                style={{ width: '100%' }}
                value={row.quantity ? String(row.quantity) : ''}
                placeholder={String(t('tms.task.drawer.containerQuantityPlaceholder'))}
                inputMode="numeric"
                onChange={(e) => {
                    const cleaned = e.target.value.replace(/[^\d]/g, '');
                    if (!cleaned) {
                        // 允许清空，内部先记为 0
                        updateRow(row.key, { quantity: 0 });
                        return;
                    }
                    const num = Number(cleaned);
                    // 至少为 1，保持原有业务含义
                    updateRow(row.key, { quantity: Math.max(1, num) });
                }}
            />
        );

        const usedUnitsInOtherRows = groupRows
            .filter((item) => item.key !== row.key && item.unit)
            .map((item) => item.unit);

        const unitInput = readOnly ? (
            <span className="text-gray-700">
                {row.unit ? String(t(CONTAINER_UNIT_OPTIONS.find((o) => o.value === row.unit)?.labelKey ?? '')) : '-'}
            </span>
        ) : (
            <Select
                style={{ width: '100%' }}
                value={row.unit || undefined}
                onChange={(val) => updateRow(row.key, { unit: (val || 'PALLET_BOX') as ContainerUnitType })}
                options={UNIT_OPTIONS_WITH_LABELS.filter(
                    (opt) => opt.value === row.unit || !usedUnitsInOtherRows.includes(opt.value)
                )}
                placeholder={String(t('common.pleaseSelect'))}
            />
        );

        const groupRowCount = groupRows.length;
        const groupIndex = groupRows.findIndex((r) => r.key === row.key);
        const groupIsOnlyRow = groupRowCount === 1;
        const groupIsLastRow = groupRowCount > 1 && groupIndex === groupRowCount - 1;
        const canAddMoreRows = groupRowCount < CONTAINER_UNIT_OPTIONS.length;

        // 卸车场景下，如果只有一条行（默认当前 hub），则这条行不能删除
        const isDefaultUnloadingSingleRow = unloading && dataSource.length === 1 && index === 0;

        /** 每组只有一条时或组内最后一条时展示添加（同组内可再加一种容器单位） */
        const showAddButton =
            !readOnly && canAddMoreRows && (groupIsOnlyRow || groupIsLastRow);

        /** 每一行都可删除，与所在组无关；但卸车场景下默认的唯一一行（当前 hub）不可删除；
         *  目的地只读时，组内最后一行不可删除（删除会导致目的地消失） */
        const showDeleteButton =
            !readOnly && !isDefaultUnloadingSingleRow && !(readOnlyDestination && groupIsOnlyRow);

        return (
            <tr key={row.key} className="bg-transparent">
                <td className="px-2 py-1">{destinationInput}</td>
                <td className="px-2 py-1">{quantityInput}</td>
                <td className="px-2 py-1">{unitInput}</td>
                <td className="px-2 py-1">
                    {!readOnly && (showAddButton || showDeleteButton) && (
                        <Space size="small">
                            {showAddButton && (
                                <Button
                                    type="text"
                                    size="small"
                                    icon={<PlusOutlined />}
                                    onClick={() => handleAddSameDestination(row)}
                                />
                            )}
                            {showDeleteButton && (
                                <Button
                                    type="text"
                                    danger
                                    size="small"
                                    icon={<MinusOutlined />}
                                    onClick={() => handleDelete(row.key)}
                                />
                            )}
                        </Space>
                    )}
                </td>
            </tr>
        );
    };

    return (
        <div className="mb-4" style={{ backgroundColor: 'transparent' }}>
            <div className="overflow-x-auto w-full">
                <table className="min-w-full text-left">
                    <thead>
                        <tr>
                            <th className="px-2 py-1 text-sm font-bold text-black">
                                {String(t('tms.task.drawer.containerTable.destination'))}
                            </th>
                            <th className="px-2 py-1 text-sm font-bold text-black">
                                {String(t('tms.task.drawer.containerTable.quantity'))}
                            </th>
                            <th className="px-2 py-1 text-sm font-bold text-black">
                                {String(t('tms.task.drawer.containerTable.unit'))}
                            </th>
                            <th className="px-2 py-1" />
                        </tr>
                    </thead>
                    <tbody>{dataSource.map((row, index) => renderRow(row, index))}</tbody>
                </table>
            </div>
            {!readOnly && !unloading && !readOnlyDestination && (
                <div className="flex justify-start mb-4">
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAddOtherDestination} className="w-full mt-2">
                        {String(t('tms.task.drawer.containerTable.addDestinationCenter'))}
                    </Button>
                </div>
            )}
        </div>
    );
};

export default ContainerInfoTable;
